// Copyright (c) 2024 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import * as Actions from "../actions"
import { TimeGroup } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { grpc } from "../../../app/rpc/executor"
import { timelineClient } from "../../../app/rpc/client"
import * as TimelineSlice from "../slices/timeline"
import { isGrpcError } from "../../../lib/rpcutils"
import { showSnackBar } from "../../snackbar/actions"

type MergeTimeGroupRpc = (payload: Actions.MergeTimeGroupsPayload) => Observable<TimeGroup>

export const mergeTimeGroups = (mergeTimeGroupsRpc: MergeTimeGroupRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.mergeTimeGroups.match),
      mergeMap(action => of(action.payload)
        .pipe(
          mergeMap(mergeTimeGroupsRpc),
          map(timeGroup => timeGroup.toObject()),
          mergeMap((timeGroup) => concat(
            of(Actions.mergeTimeGroupsFulfilled()),
            of(TimelineSlice.deleteTimeGroups(action.payload.request.getTimeGroupIdsList())),
            of(TimelineSlice.upsertTimeGroup(timeGroup)),
            of(TimelineSlice.setTimeRowsGroupId({
              timeRowIds: action.payload.timeRowIds,
              timeGroupId: timeGroup.id,
            })),
            of(Actions.generateTimesheetFieldConfigs([timeGroup])),
            of(TimelineSlice.replaceActiveTimeGroupsIds({ timeGroupsIds: [timeGroup.id], })),
          )),
          catchError(handleError),
        )
      ),
    )

const mergeTimeGroupsRpc = (payload: Actions.MergeTimeGroupsPayload): Observable<TimeGroup> =>
  grpc(() => timelineClient.mergeTimeGroups(payload.request, null))

const handleError = (e: unknown): Observable<Action> => {
  const actions: Action[] = [
    Actions.mergeTimeGroupsErrored(),
  ]
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.FAILED_PRECONDITION) {
      actions.push(
        showSnackBar({
          message: t({ id: "msg_merge_time_groups_failed_precondition", message: "Failed to merge timesheet entries. Timesheet entries may be out of sync." }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      actions.push(showSnackBar({
        message: t({ id: "msg_merge_time_groups_permission_denied", message: "Failed to merge timesheet entries. You do not have permission." }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({ id: "msg_merge_time_groups_failed", message: "Failed to merge timesheet entries. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

export default mergeTimeGroups(mergeTimeGroupsRpc)
